import { BaseDragger } from "./BaseDragger";
import type {
  DraftSegmentInfo,
  Point2D,
  Point3D,
  SegmentDelta,
  TypedInputSpec,
} from "./types";
import {
  RELATIVE_POINT_MODES,
  deltaFromRelativeInput,
} from "./relativePointInput";
import { hasMinimumSeparation } from "./pointGeometry";
import { isShortcutBlockingTarget } from "@/utils/keyboardGuards";

/**
 * Abstract base for draggers that place a shape from exactly two points,
 * each fixed by its own press-drag-release gesture (point A on the first
 * release, point B + entity creation on the second) — the same tablet-first
 * interaction model as press-drag-release, just for a fixed pair of points
 * instead of a continuous single stroke.
 *
 * Deliberately independent from SequentialDragger: the two only share a
 * couple of one-line ideas (snap reference = last fixed point, reproject
 * after pan/zoom), while SequentialDragger's actual complexity — incremental
 * per-point undo commands, unbounded point count, close-on-click-near-start —
 * doesn't apply here (nothing is committed before point B exists).
 *
 * Subclasses implement:
 * - createPreview(): create the initial preview overlay once point A is fixed
 * - updatePreview(): update the preview while point B is being positioned
 * - createEntity(): build the actual entity from point A and point B
 */
export abstract class TwoPointDragger extends BaseDragger {
  private pointAScreen: Point2D | null = null;
  private pointAWorld: Point3D | null = null;

  private previewIds: string[] = [];

  private keyDownHandler: ((ev: KeyboardEvent) => void) | null = null;
  private unsubscribeViewChanged: (() => void) | null = null;

  private pendingChangeHandlers: Array<(pending: boolean) => void> = [];

  // Live draft segment (point A + delta to the current drag/typed position),
  // for UI like a typed-measurement input panel.
  private draftInfo: DraftSegmentInfo | null = null;

  // The most recent typed (not dragged) point B, if any — remembered so the
  // preview can be redrawn at the correct screen position after a zoom/pan,
  // since (unlike a real drag) no further pointer event will come along to
  // naturally refresh it. Stored as a world point rather than a delta because
  // that's exactly what updatePreview() consumes.
  private lastTypedWorld: Point3D | null = null;

  // ============== Lifecycle ==============

  protected onActivate(): void {
    this.keyDownHandler = (ev: KeyboardEvent) => {
      if (isShortcutBlockingTarget(ev.target, ev.key)) {
        return;
      }

      if (ev.key === "Escape") {
        this.cancelPendingPlacement();
      }
    };
    window.addEventListener("keydown", this.keyDownHandler);

    this.unsubscribeViewChanged = this.viewer.onViewChanged(() => {
      if (!this.pointAWorld) return;
      // Wait a frame so the new view transform is applied before reprojecting.
      requestAnimationFrame(() => this.reprojectPendingPoint());
    });

    // The spec depends on isActive, so tell the keypad it just became available.
    this.emitTypedInputChange();
  }

  protected onDeactivate(): void {
    if (this.keyDownHandler) {
      window.removeEventListener("keydown", this.keyDownHandler);
      this.keyDownHandler = null;
    }
    if (this.unsubscribeViewChanged) {
      this.unsubscribeViewChanged();
      this.unsubscribeViewChanged = null;
    }
    this.emitTypedInputChange();
  }

  // ============== Dragger Callbacks ==============

  protected onStart(screen: Point2D, world: Point3D): void {
    // A pointer gesture supersedes any typed preview.
    this.lastTypedWorld = null;
    if (this.pointAWorld) {
      // Placing point B: show a live preview immediately in case this gesture is a real drag.
      this.updatePreview(screen, world);
      this.emitDraftFor(world);
      this.viewer.update();
    }
    // No anchor yet: point A is always taken from where this gesture *ends*, not starts.
  }

  protected onDrag(screen: Point2D, world: Point3D): void {
    if (!this.pointAWorld) return;
    this.updatePreview(screen, world);
    this.emitDraftFor(world);
    this.viewer.update();
  }

  protected onEnd(screen: Point2D, world: Point3D): void {
    if (!this.pointAScreen || !this.pointAWorld) {
      // This release fixes point A.
      this.pointAScreen = screen;
      this.pointAWorld = world;
      this.createPreview(screen, world);
      // Zero-delta "reset frame": the typed-measurement panel reads this as
      // "a new segment starts here", clearing any stale field values.
      this.setDraftSegment({
        origin: world,
        dx: 0,
        dy: 0,
        length: 0,
        angleDeg: 0,
      });
      this.viewer.update();
      this.emitPendingPlacementChange(true);
      return;
    }

    // This release fixes point B.
    const aScreen = this.pointAScreen;
    const aWorld = this.pointAWorld;

    if (!hasMinimumSeparation(aScreen, screen)) {
      // Degenerate/accidental click on top of point A: discard, don't create a zero-size shape.
      this.clear();
      this.viewer.update();
      return;
    }

    this.clearPreview();
    this.createEntity(aWorld, world);
    this.viewer.update();
    this.clear();
  }

  // Inherited default `onCancel()` (BaseDragger) already calls `this.clear()`,
  // which is exactly right here: discard the pending point A, nothing was ever committed.

  /**
   * Pure mouse hover (no button held) while point A is pending — keeps the
   * point B preview following the cursor between the two clicks, AutoCAD-style,
   * instead of requiring a second press-drag-release to see it move.
   */
  override handleHoverMove(screen: Point2D, world: Point3D): void {
    if (!this.pointAWorld) return;
    // Real pointer movement supersedes any typed preview, same as onStart().
    this.lastTypedWorld = null;
    this.updatePreview(screen, world);
    this.emitDraftFor(world);
    this.viewer.update();
  }

  /**
   * The pointer left the canvas mid-drag. If point A isn't fixed yet,
   * nothing has been committed — no-op. If point A is fixed and point B is
   * being dragged, discard the in-progress point B and reset the preview
   * back to a zero-length stub at point A, without clearing pointAWorld so
   * point A stays pending (hasPendingPlacement()/cancel button still show it).
   *
   * updatePreview() alone isn't enough here: subclasses that show live
   * width/height/length labels (see clearLiveMeasurements()) redraw them
   * unconditionally, even at zero size — so those labels are cleared
   * explicitly afterward instead of being left showing a meaningless "0".
   */
  override handleLeave(): void {
    if (!this.isActive || !this.isDragging) return;
    this.isDragging = false;

    if (this.pointAWorld && this.pointAScreen) {
      this.updatePreview(this.pointAScreen, this.pointAWorld);
      this.clearLiveMeasurements();
      this.setDraftSegment({
        origin: this.pointAWorld,
        dx: 0,
        dy: 0,
        length: 0,
        angleDeg: 0,
      });
      this.viewer.update();
    }
  }

  /**
   * Clear any live measurement overlay (e.g. width/height/length labels)
   * shown while point B is being dragged. Called by handleLeave() after
   * resetting the shape preview, since updatePreview() itself always
   * redraws them. No-op by default; override in subclasses that track
   * their own MeasurementLabels instance.
   */
  protected clearLiveMeasurements(): void {}

  // ============== Snap ==============

  override getSnapReferencePoint(): Point3D | null {
    return this.pointAWorld;
  }

  // Points are fixed on release: where the drag starts is not one of them.
  override snapsToGestureStart(): boolean {
    return false;
  }

  // ============== Pending Placement (cancel button for touch/tablet) ==============

  override hasPendingPlacement(): boolean {
    return !!this.pointAWorld;
  }

  override onPendingPlacementChange(
    handler: (pending: boolean) => void,
  ): () => void {
    this.pendingChangeHandlers.push(handler);
    return () => {
      const index = this.pendingChangeHandlers.indexOf(handler);
      if (index >= 0) {
        this.pendingChangeHandlers.splice(index, 1);
      }
    };
  }

  override cancelPendingPlacement(): void {
    if (!this.pointAWorld) return;
    this.clear();
    this.viewer.update();
  }

  private emitPendingPlacementChange(pending: boolean): void {
    for (const handler of this.pendingChangeHandlers) {
      handler(pending);
    }
  }

  // ============== Draft Segment / Typed Input ==============

  /**
   * Set the current draft segment and notify subscribers (e.g. a typed-
   * measurement input panel). Called whenever point A is fixed or discarded
   * (delta reset to 0 / null) and whenever a drag or typed preview moves the
   * point B end.
   */
  protected setDraftSegment(info: DraftSegmentInfo | null): void {
    this.draftInfo = info;
    this.emitTypedInputChange();
  }

  /**
   * Point B, typed relative to point A. Offered whenever this engine is active,
   * which is exactly the old availability rule (the pad was only offered in
   * point-by-point mode): in single-stroke mode DualModeDragger never activates
   * this engine, so the spec stays null. Before point A exists the fields are
   * empty and preview/commit are no-ops.
   */
  override getTypedInputSpec(): TypedInputSpec | null {
    if (!this.isActive) return null;
    const draft = this.draftInfo;
    return {
      titleKey: "canvas:tools.options.keypad.title.measure",
      // The vertex being measured from: moving it starts a new segment.
      stepId: draft ? `${draft.origin.x},${draft.origin.y}` : "",
      modes: RELATIVE_POINT_MODES,
      values: draft
        ? {
            dx: draft.dx,
            dy: draft.dy,
            length: draft.length,
            angleDeg: draft.angleDeg,
          }
        : {},
    };
  }

  override previewTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    const delta = deltaFromRelativeInput(modeId, values);
    if (delta) this.previewTypedSegment(delta);
  }

  override commitTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    const delta = deltaFromRelativeInput(modeId, values);
    if (delta) this.commitTypedSegment(delta);
  }

  /** Emit the draft segment running from point A to the given point B. */
  private emitDraftFor(world: Point3D): void {
    const origin = this.pointAWorld;
    if (!origin) return;
    const dx = world.x - origin.x;
    const dy = world.y - origin.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const angleDeg = length > 0.0001 ? Math.atan2(dy, dx) * (180 / Math.PI) : 0;
    this.setDraftSegment({ origin, dx, dy, length, angleDeg });
  }

  /** World point B corresponding to a typed delta from point A, if point A exists. */
  private typedPointB(delta: SegmentDelta): Point3D | null {
    const origin = this.pointAWorld;
    if (!origin) return null;
    return { x: origin.x + delta.dx, y: origin.y + delta.dy, z: origin.z };
  }

  private previewTypedSegment(delta: SegmentDelta): void {
    const world = this.typedPointB(delta);
    if (!world) return;
    this.lastTypedWorld = world;
    // The typed world point is passed straight through — unlike the polyline
    // case, updatePreview() takes the world point itself, so there's no
    // screenToWorld round-trip to lose precision to.
    this.updatePreview(this.viewer.worldToScreen(world), world);
    this.emitDraftFor(world);
    this.viewer.update();
  }

  private commitTypedSegment(delta: SegmentDelta): void {
    const origin = this.pointAWorld;
    const world = this.typedPointB(delta);
    if (!origin || !world) return;
    // Ignore a no-op commit (e.g. Enter pressed before typing anything) —
    // don't create a degenerate zero-size shape.
    if (delta.dx === 0 && delta.dy === 0) return;

    // Deliberately bypasses onEnd's hasMinimumSeparation() check: that's a
    // pixel-distance heuristic for a release gesture landing on top of point A
    // by accident, not something a typed exact value should be second-guessed
    // through (a deliberate 1 mm shape is legitimate).
    this.clearPreview();
    this.createEntity(origin, world);
    this.viewer.update();
    this.clear();
  }

  // ============== Reprojection ==============

  private reprojectPendingPoint(): void {
    if (!this.pointAWorld) return;
    this.pointAScreen = this.viewer.worldToScreen(this.pointAWorld);
    if (this.lastTypedWorld) {
      // A typed preview has no pointer behind it: nothing else would ever
      // redraw it at the new view transform.
      this.updatePreview(
        this.viewer.worldToScreen(this.lastTypedWorld),
        this.lastTypedWorld,
      );
      return;
    }
    // Re-anchor the preview at point A's new screen position; the next real
    // pointer move will bring the point B end back in sync with the cursor.
    this.updatePreview(this.pointAScreen, this.pointAWorld);
  }

  // ============== Abstract Methods ==============

  /** Create the initial preview overlay. Called once point A is fixed. */
  protected abstract createPreview(screen: Point2D, world: Point3D): void;

  /** Update the preview overlay while point B is being positioned. */
  protected abstract updatePreview(screen: Point2D, world: Point3D): void;

  /** Create the actual entity from point A and point B. Called once point B is fixed. */
  protected abstract createEntity(a: Point3D, b: Point3D): void;

  // ============== Utility Methods ==============

  protected addPreviewId(id: string): void {
    this.previewIds.push(id);
  }

  protected clearPreview(): void {
    const overlay = this.viewer.overlayManager;
    if (overlay) {
      for (const id of this.previewIds) {
        overlay.remove(id);
      }
    }
    this.previewIds = [];
  }

  protected clear(): void {
    super.clear();
    this.clearPreview();
    const wasPending = !!this.pointAWorld;
    this.pointAScreen = null;
    this.pointAWorld = null;
    this.lastTypedWorld = null;
    this.setDraftSegment(null);
    if (wasPending) {
      this.emitPendingPlacementChange(false);
    }
  }
}
